import {
  filter, isNil, map, uniq,
} from 'lodash';
import { configureScope } from '@sentry/react';
import { setUserId } from 'firebase/analytics';
import { signInAnonymously } from 'firebase/auth';
import { batch } from 'react-redux';
import { setAcceptsCookies, trackAnalytics } from '@client/intercom';
import {
  startUpdateUserAcceptsCookies, setAnonymousUser, updateCurrentSessionUser, startScaffoldingUser,
  setCurrentSessionUser, startWatchUser, startRefetchFirebaseToken, startSetSelectedProfile, startFetchUserLocation, updatePosition,
} from '@client/slices/helpers/session.reducer';
import { loadCategories } from '@client/slices/entities/categories.reducer';
import { enrichUser, fetchFirebaseToken, generateAnonymousUserLocationHash } from '@client/listeners/utils/session';
import { setFirebaseLoggedInState, setFirebaseScaffolded } from '@client/slices/helpers/clients.reducer';
import { firebaseRequest, handleRequest } from '@client/listeners/lib/requests';
import { showSnackbar, showMessageSuccessSnackbar } from '@client/listeners/lib/snackbar';
import { getBeaconById } from '@client/selectors/entities/beacons';
import { getSelectedProfile, getSelectedProfileId } from '@client/selectors/helpers/profile';
import { hasListener } from '@client/selectors/helpers/listeners';
import { addMessage } from '@client/slices/entities/messages.reducer';
import { startCreateShowCallThread, upsertThread, setCurrentThread } from '@client/slices/entities/threads.reducer';
import { addFirestoreListener, removeFirestoreListener } from '@client/slices/helpers/listeners.reducer';
import { setLoaderIsLoading, setLoaderIsFinishedSuccess, setLoaderIsFinishedError } from '@client/slices/helpers/loaders.reducer';
import { ProfileTypes, ThreadEventTypes, NotificationTypes } from '@sda/enums';
import { apiUrl, buildURIFromParams } from '@sda/utils/url';
import { upsertUnseenNotification } from '@client/slices/entities/notifications.reducer';
import { AccountStatuses } from '@sda/enums/hyperwallet';
import { getAnonymousUser, getSessionUserState, getUserId } from '@client/selectors/helpers/login';
import { sessionUserCookiesState, sessionUserSelector } from '@client/selectors';
import { getCurrentPosition, getSessionUserActivityName } from '@client/selectors/helpers/session';
import { normalizeStringArray } from '@sda/utils/crypto';
import type { RootState, AppDispatch } from '@client/store';
import type { PrivateUser } from '@sda/interfaces/user';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { AddListener } from '@client/listeners/lib';

interface GeolocationResponse {
  readonly accuracy: number;
  readonly location: {
    readonly lat: number;
    readonly lng: number;
  };
}

const scaffoldSelectedProfile = (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  selectedProfileData: Pick<PrivateUser, 'selectedProfileId' | 'selectedProfileType'>,
): void => {
  const firebase = window.Firebase;
  const { selectedProfileId, selectedProfileType } = selectedProfileData;

  if (selectedProfileId) {
    if (selectedProfileType === ProfileTypes.ACT) {
      firebase.SDK.Acts.Private().listen(
        selectedProfileId,
        null,
        { modifier: { privateFetched: true } },
      );
    } else if (selectedProfileType === ProfileTypes.VENUE) {
      firebase.SDK.Venues.Private().listen(
        selectedProfileId,
        null,
        { modifier: { privateFetched: true } },
      );
    } else if (selectedProfileType === ProfileTypes.PENDING) {
      firebase.SDK.Profiles.Private().listen(
        selectedProfileId,
        null,
        { modifier: { privateFetched: true } },
      );
    }
  }
};

export const allUsersListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startWatchUser,
    effect: (_, listenerApi) => {
      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      // Fetch two user IDs in case this is an admin impersonating another user
      const userId = getUserId(state);
      const userData = sessionUserSelector(state);

      const { uid, createdAt } = userData;

      if (createdAt) {
        listenerApi.fork(async () => {
          await enrichUser(listenerApi);
        });
      }

      const firebase = window.Firebase;

      firebase.SDK.Permissions.listen(
        uid,
        (permissions) => {
          dispatch(updateCurrentSessionUser(permissions));
          dispatch(startRefetchFirebaseToken(permissions));
        },
      );

      firebase.SDK.Users.listen(userId, (user) => dispatch(updateCurrentSessionUser({
        ...user,
        publicFetched: true,
      })));

      firebase.SDK.Users.Private().listen(
        uid,
        (user) => {
          if (user.selectedProfileId) {
            scaffoldSelectedProfile(listenerApi, user);
          }

          dispatch(updateCurrentSessionUser({
            ...user,
            privateFetched: true,
          }));
        },
      );

      const userPendingProfileListener = hasListener(state, 'watch-user-pending-profiles');

      if (!userPendingProfileListener) {
        const watchListener = firebase.SDK.Profiles.watch(
          {
            queries: (query) => [query.contains('collaborators', userId)],
            modifier: { publicFetched: true },
          },
        );

        dispatch(addFirestoreListener({ watchListener, name: 'watch-user-pending-profiles' }));
      }

      const userVenueListener = hasListener(state, 'watch-user-venues');

      if (!userVenueListener) {
        const watchListener = firebase.SDK.Venues.watch(
          {
            queries: (query) => [query.contains('collaborators', userId)],
            modifier: { publicFetched: true },
          },
        );

        dispatch(addFirestoreListener({ watchListener, name: 'watch-user-venues' }));
      }

      const userActListener = hasListener(state, 'watch-user-acts');

      if (!userActListener) {
        const watchListener = firebase.SDK.Acts.watch(
          {
            queries: (query) => [query.contains('collaborators', userId)],
            modifier: { publicFetched: true },
          },
        );

        dispatch(addFirestoreListener({ watchListener, name: 'watch-user-acts' }));
      }

      // get all unread thread notifications
      // this enables the notification badge next to the Messages icon
      const userUnreadNotificationListener = hasListener(state, 'watch-user-unseen-notifications');

      if (!userUnreadNotificationListener) {
        const watchListener = firebase.SDK.Notifications.watch(
          {
            queries: (query) => [
              query.isEqualTo('owner', userId),
              query.isFalse('read'),
              query.isTrue('metadata.usesNewConnect'),
            ],
          },
          (notification) => dispatch(upsertUnseenNotification({ ...notification, modifier: { publicFetched: true } })),
        );

        dispatch(addFirestoreListener({ watchListener, name: 'watch-user-unseen-notifications' }));
      }

      const userHyperwalletAccountListener = hasListener(state, 'watch-user-hyperwallet-account');

      if (!userHyperwalletAccountListener) {
        const watchListener = firebase.SDK.Users.Private().HyperwalletAccounts(userId).watch(
          {
            queries: (query) => [query.isNotEqualTo('userStatus', AccountStatuses.DE_ACTIVATED)],
          },
        );

        dispatch(addFirestoreListener({ watchListener, name: 'watch-user-hyperwallet-account' }));
      }
    },
  }),

  addListener({
    actionCreator: startScaffoldingUser,
    effect: async (action, listenerApi) => {
      const authUser = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const firebase = window.Firebase;

      const acceptsCookies = sessionUserCookiesState(state);
      const anonymousUser = getAnonymousUser(state);

      if (authUser) {
        const { isAnonymous } = authUser;

        if (!isAnonymous) {
          const token = await authUser.getIdTokenResult();

          const userData = {
            uid: authUser.uid,
            isAnonymous: false,
            email: authUser.email,
            emailVerified: authUser.emailVerified || false,
            metadata: authUser.metadata,
            providerData: authUser.providerData, // @todo: remove this... temp solution until transition is created.
            ...token.claims,
          };

          if (process.env.NODE_ENV !== 'development') {
            configureScope((scope) => {
              scope.setUser({
                id: userData.uid,
                email: userData.email || '',
              });
            });
          }

          batch(() => {
            dispatch(updateCurrentSessionUser(userData));
            dispatch(setFirebaseLoggedInState(true));
            dispatch(setFirebaseScaffolded(true));
            dispatch(startUpdateUserAcceptsCookies(acceptsCookies));
            dispatch(startWatchUser());

            if (anonymousUser !== null) {
              dispatch(setAnonymousUser(null));
            }
          });
        } else {
          batch(() => {
            dispatch(setCurrentSessionUser(false));
            dispatch(setAnonymousUser(authUser));
            dispatch(setFirebaseLoggedInState(false));
            dispatch(setFirebaseScaffolded(true));
          });

          listenerApi.fork(async () => {
            await generateAnonymousUserLocationHash(listenerApi);
          });
        }

        setUserId(window.Firebase.analytics, authUser.uid);
      } else {
        batch(() => {
          dispatch(setCurrentSessionUser(false));
          dispatch(setFirebaseLoggedInState(false));
          dispatch(setFirebaseScaffolded(true));

          if (anonymousUser !== null) {
            dispatch(setAnonymousUser(null));
          }
        });

        if (!anonymousUser) {
          await signInAnonymously(firebase.auth);
        }
      }
    },
  }),

  addListener({
    actionCreator: startUpdateUserAcceptsCookies,
    effect: async (action, listenerApi) => {
      const acceptsCookies = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const sessionUser = getSessionUserState(state);
      const anonymousUser = getAnonymousUser(state);

      // anonymous user cookies response
      if (anonymousUser) {
        dispatch(setAnonymousUser({ acceptsCookies }));
      } else {
        const userId = getUserId(state);

        // authenticated user cookies response
        if (sessionUser && userId) {
          const firebase = window.Firebase;

          try {
            const user = await firebase.SDK.Users.Private().get(userId);

            if (user) {
              if (isNil(user.acceptsCookies)
              || (!isNil(user.acceptsCookies) && user.acceptsCookies !== acceptsCookies && acceptsCookies === true)) {
                await firebase.SDK.Users.Private().update(userId, { acceptsCookies });

                dispatch(updateCurrentSessionUser({ acceptsCookies }));

                setAcceptsCookies(acceptsCookies);
                trackAnalytics(startUpdateUserAcceptsCookies, { acceptsCookies });
              }
            }
          } catch (e) {
            console.error(e);
          }
        }
      }
    },
  }),

  addListener({
    actionCreator: loadCategories,
    effect: () => {
      const firebase = window.Firebase;

      const categories = firebase.SDK.Categories;

      categories.ActTypes().watch({});
      categories.Badges().watch({});
      categories.Countries().watch({});
      categories.Disabilities().watch({});
      categories.Genders().watch({});
      categories.Genres().watch({});
      categories.Identities().watch({});
      categories.Sexualities().watch({});
      categories.MusicTypes().watch({});
      categories.ShowSalesRestrictions().watch({});
      categories.VenueTypes().watch({});
      categories.VenueSizes().watch({});
    },
  }),

  addListener({
    actionCreator: startRefetchFirebaseToken,
    effect: async (action, listenerApi) => {
      const permissions = action.payload;
      const firebase = window.Firebase;

      if (firebase.auth.currentUser) {
        const token = await firebase.auth.currentUser.getIdTokenResult();

        if (!!token.claims.isAdmin !== permissions.isAdmin) {
          await fetchFirebaseToken(listenerApi);
        }
      }
    },
  }),

  addListener({
    actionCreator: startCreateShowCallThread,
    effect: async (action, listenerApi) => {
      const { beaconId, message } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;
      const firebase = window.Firebase;

      const scope = 'send-showcall-message';
      dispatch(setLoaderIsLoading(scope));

      try {
        const { profileData, profileId, profileType } = getBeaconById(state, beaconId);
        const selectedProfile = getSelectedProfile(state);
        const userId = getUserId(state);

        if (profileType === selectedProfile.profileType) {
          throw new Error('Two profiles of the same type cannot create a show together at this time.');
        }

        const collaborators = uniq([
          ...profileData.collaborators || [],
          ...selectedProfile.collaborators || [],
        ]);

        const act = profileType === ProfileTypes.ACT ? { ...profileData, id: profileId } : selectedProfile;
        const venue = profileType === ProfileTypes.VENUE ? { ...profileData, id: profileId } : selectedProfile;

        const existingThread = await firebase.SDK.Threads.getBy((query) => [
          query.isEqualTo('profileHash', normalizeStringArray([act.id, venue.id])),
          query.isTrue('isMasterThread'),
        ]);

        if (existingThread) {
          const messageData = firebase.SDK.Messages.filter({
            collaborators,
            destinationBeaconId: beaconId,
            masterThreadId: existingThread.id,
            threadId: existingThread.id,
            body: message,
            owner: userId,
            eventType: ThreadEventTypes.THREAD_EVENT_MESSAGE,
            profileId: selectedProfile.id,
            profileSubscribers: existingThread.profileIds,
          }, firebase.SDK.Messages.PrivateMap);

          const fromName = getSessionUserActivityName(state);
          const recipients = filter(collaborators, (collaborator) => collaborator !== userId);

          const messageId = firebase.SDK.Messages.getNextId();

          await firebase.helpers.runTransaction(async () => {
            await firebase.SDK.Threads.update(existingThread.id, { profileSubscribers: existingThread.profileIds });
            await firebase.SDK.Messages.create(messageData, messageId);

            await Promise.all(recipients.map((recipientId) => firebase.SDK.Notifications.addNotification(
              recipientId,
              NotificationTypes.NOTIFICATION_TYPE_MESSAGE,
              {
                messageId,
                fromName,
                beaconId,
                threadId: existingThread.id,
                profileIds: existingThread.profileIds,
                showId: existingThread.showId,
                usesNewConnect: existingThread.usesNewConnect || false,
              },
            )));
          });

          batch(() => {
            dispatch(addMessage({ ...messageData, id: messageId }));
            dispatch(setLoaderIsFinishedSuccess(scope, existingThread.id));
          });

          showMessageSuccessSnackbar(existingThread.id);
        } else {
          const beacon = getBeaconById(state, beaconId);
          const profileIds = [act.id, venue.id];

          // Now create the thread
          const threadId = firebase.SDK.Threads.getNextId();
          const threadData = firebase.SDK.Threads.filter({
            beaconId,
            collaborators,
            profileIds,
            owner: userId,
            publicShow: null,
            showId: null,
            usesNewConnect: true,
            isMasterThread: true,
            hasConnection: act.owner === venue.owner,
            publicBeacon: firebase.SDK.Beacons.filter(beacon, firebase.SDK.Beacons.PublicMap),
            profileSubscribers: profileIds,
            profileHash: normalizeStringArray(profileIds),
            publicProfiles: [
              { profileId: act.id, profileType: ProfileTypes.ACT },
              { profileId: venue.id, profileType: ProfileTypes.VENUE },
            ],
          }, firebase.SDK.Threads.PublicMap);

          const messageId = firebase.SDK.Messages.getNextId();
          const messageData = firebase.SDK.Messages.filter({
            threadId,
            collaborators,
            destinationBeaconId: beaconId,
            masterThreadId: threadId,
            body: message,
            owner: userId,
            eventType: ThreadEventTypes.THREAD_EVENT_MESSAGE,
            profileId: selectedProfile.id,
            profileSubscribers: profileIds,
          }, firebase.SDK.Messages.PrivateMap);

          const recipients = filter(collaborators, (collaborator) => collaborator !== userId);
          const fromName = getSessionUserActivityName(state);

          await firebase.helpers.runTransaction(async () => {
            await firebase.SDK.Threads.set(threadId, threadData);
            await firebase.SDK.Messages.set(messageId, messageData);

            await Promise.all(map(recipients, (recipientId) => firebase.SDK.Notifications.addNotification(
              recipientId,
              NotificationTypes.NOTIFICATION_TYPE_MESSAGE,
              {
                fromName,
                messageId,
                profileIds,
                threadId,
                showId: null,
                usesNewConnect: true,
              },
            )));
          });

          const request = await firebaseRequest({
            url: apiUrl(`beacon/${beaconId}/response`),
            method: 'PUT',
          });

          listenerApi.fork(async () => {
            await handleRequest(request);
          });

          batch(() => {
            dispatch(addMessage({ ...messageData, id: messageId }));
            dispatch(upsertThread({ ...threadData, id: threadId }));
            dispatch(setLoaderIsFinishedSuccess(scope, threadId));
          });

          showMessageSuccessSnackbar(threadId);

          trackAnalytics(startCreateShowCallThread, threadData);
        }
      } catch (e) {
        console.error(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

  addListener({
    actionCreator: startSetSelectedProfile,
    effect: async (action, listenerApi) => {
      const { profileId, profileType, scope = `select-profile-${profileId}` } = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      dispatch(setLoaderIsLoading(scope));

      try {
        const currentProfileId = getSelectedProfileId(state);

        if (currentProfileId !== profileId) {
          const firebase = window.Firebase;

          const { uid: userId, selectedProfileType: currentSelectedProfileType } = sessionUserSelector(state);

          const updateData = {
            selectedProfileId: profileId,
            selectedProfileType: profileType,
          };

          await firebase.SDK.Users.Private().update(userId, updateData);

          if (profileType === ProfileTypes.ACT) {
            await firebase.SDK.Acts.Private().update(profileId, {
              lastOnlineAt: firebase.helpers.timestamp(),
            });
          } else if (profileType === ProfileTypes.VENUE) {
            await firebase.SDK.Venues.Private().update(profileId, {
              lastOnlineAt: firebase.helpers.timestamp(),
            });
          } else if (profileType === ProfileTypes.PENDING) {
            await firebase.SDK.Profiles.Private().update(profileId, {
              lastOnlineAt: firebase.helpers.timestamp(),
            });
          }

          scaffoldSelectedProfile(listenerApi, updateData);

          batch(() => {
            dispatch(setCurrentThread(null));
            dispatch(updateCurrentSessionUser(updateData));
            dispatch(removeFirestoreListener(`watch-profile-beacons-${currentProfileId}`));
          });

          if (currentProfileId && currentSelectedProfileType === ProfileTypes.ACT) {
            firebase.SDK.Acts.Private().unsubscribe(currentProfileId);
          } else if (currentProfileId && currentSelectedProfileType === ProfileTypes.VENUE) {
            firebase.SDK.Venues.Private().unsubscribe(currentProfileId);
          } else if (currentProfileId && currentSelectedProfileType === ProfileTypes.PENDING) {
            firebase.SDK.Profiles.Private().unsubscribe(currentProfileId);
          }
        }

        dispatch(setLoaderIsFinishedSuccess(scope));
      } catch (e) {
        console.error(e);
        dispatch(setLoaderIsFinishedError(scope, e));
        showSnackbar(e, 'error');
      }
    },
  }),

  addListener({
    actionCreator: startFetchUserLocation,
    effect: async (_, listenerApi) => {
      const state = listenerApi.getState();
      const currentPosition = getCurrentPosition(state);

      if (currentPosition) {
        listenerApi.unsubscribe();
      } else {
        try {
          const { dispatch } = listenerApi;
          const googleMapsGeolocationUrl = buildURIFromParams('https://www.googleapis.com/geolocation/v1/geolocate', { key: process.env.GOOGLE_MAPS_API_KEY });

          const response = await fetch(googleMapsGeolocationUrl, {
            method: 'POST',
            headers: new Headers({
              'Content-Type': 'application/json',
              Accept: 'application/json',
            }),
            body: JSON.stringify({ considerIp: true }),
          });

          const responseJson: GeolocationResponse = await response.json();

          if (responseJson?.location) {
            dispatch(updatePosition({
              accuracy: responseJson.accuracy,
              latitude: responseJson.location.lat,
              longitude: responseJson.location.lng,
            }));
          }
        } catch (e) {
          console.error(e);
        }
      }
    },
  }),
];
